import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type FilterOperatorObject,
  type FilterOption,
  type FilterOptionsParams,
} from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import type {
  AggregatedPackage,
  AggregatedRepository,
  AgentCommandEventType,
  AgentCommandFilterOptionField,
} from '@kirin/api-client/contracts/agent-command-events';
import {
  agentCommandFilterOptionsQueryKey,
  fetchAgentCommandEventFilterOptions,
} from './api';
import {
  AgentFilterOption,
  AgentsCell,
  BranchCell,
  CountButton,
  ProjectCell,
} from './CellRenderers';
import { PACKAGE_VERSION_TOOLTIP } from './constants';
import { OsvVulnerabilityCell } from './OsvVulnerabilityCell';
import { PackageStateCell } from './PackageStateCell';
import type { PackageListActions } from '@/hooks/api/usePackageListActions';
import { ClickableUserCount } from '@/components/shared/plugins/ClickableUserCount';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { endpointFilterConfig } from '@/components/shared/shadow-ai';

const DASH = '—';
const FILTER_OPTIONS_PAGE_SIZE = 10;

function text(value: string | null): string {
  return value && value.trim() ? value : DASH;
}

// Multi-select distinct-value dropdown (chips) + free-text contains — the
// Projects/Inventory string-filter behavior. Backend `string` field type maps
// INCLUDES/EXCLUDES to exact `lower() IN (...)` and CONTAINS to ILIKE.
const STRING_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
];

// Date range operators for the Last-seen column (backend `date` filter).
const DATE_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.GREATER_THAN, label: 'Is after' },
  { value: FilterOperators.LESS_THAN, label: 'Is before' },
  { value: FilterOperators.BETWEEN, label: 'Is between' },
];

const VALUELESS_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.IS_EMPTY, label: 'Is empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is not empty' },
];

// Numeric range operators for the aggregated Users / Occurrences count columns
// (backend `number` HAVING filter) — mirrors the alerts occurrenceCount column.
const NUMBER_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...VALUELESS_OPERATORS,
];

/**
 * Shared string-column filter config: a server-backed distinct-value multi-select
 * dropdown scoped to the current org (or the personal account when
 * `organizationId` is undefined).
 */
function stringFilter(
  field: AgentCommandFilterOptionField,
  organizationId: string | undefined,
  eventType: AgentCommandEventType
) {
  return {
    filterable: true as const,
    dataType: ColumnDataTypes.STRING,
    filterOperators: STRING_OPERATORS,
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: (params: FilterOptionsParams) =>
        agentCommandFilterOptionsQueryKey(
          organizationId,
          eventType,
          field,
          params
        ),
      queryFn: (params: FilterOptionsParams) =>
        fetchAgentCommandEventFilterOptions(
          organizationId,
          eventType,
          field,
          params
        ),
      queryConfig: { staleTime: 2 * 60 * 1000 },
      pagination: { mode: 'offset' as const, limit: FILTER_OPTIONS_PAGE_SIZE },
    },
  };
}

export interface AggregatedPackageColumnActions {
  /** Open the unaggregated occurrences list for a group. */
  onShowOccurrences: (row: AggregatedPackage) => void;
  /** Open the distinct-users list for a group (KIR-3833). */
  onShowUsers: (row: AggregatedPackage) => void;
  /** Open the distinct-endpoints list for a group (KIR-3833). */
  onShowEndpoints: (row: AggregatedPackage) => void;
  /** Allow/Block list actions for the State column (KIR-4443, AC 9). */
  packageListActions: PackageListActions;
  /** Whether the viewer may change the Allow/Block lists. */
  canEditPackageLists: boolean;
  /**
   * Whether the Enforcement column is shown at all: the Packages policy is
   * behind the `malicious_packages` flag, and its rules only describe what
   * the scanner does in some scopes (see AgentsActivityTab).
   */
  showPackageLists: boolean;
}

/**
 * Columns for the aggregated Packages view (KIR-3807): one row per
 * package+source+project+version. Agent is a multi-chip cell; Users and
 * Occurrences are sortable/filterable counts; Vulnerabilities is a count that
 * opens the vuln list (not sortable — enriched post-query). Default sort is
 * last-seen descending (set on the DataTable, see index.tsx).
 */
export function createAggregatedPackageColumns(
  organizationId: string | undefined,
  actions: AggregatedPackageColumnActions
): ColumnConfig<AggregatedPackage>[] {
  const eventType: AgentCommandEventType = 'package';
  const agentFilter = stringFilter('agentName', organizationId, eventType);
  const userFilter = stringFilter('userEmail', organizationId, eventType);
  const endpointFilter = stringFilter(
    'endpointName',
    organizationId,
    eventType
  );
  const listStateColumn: ColumnConfig<AggregatedPackage> = {
    // KIR-4443 (AC 8). Sits beside the OSV cell because the two together
    // answer "should this be here": OSV says what the package IS,
    // Enforcement says what the organization has DECIDED about it.
    //
    // Not sortable and not column-filterable: it is derived per request
    // from the org's rule set rather than selected from a column, so there
    // is nothing for AGG_SORT_COLUMNS or /filter-options to key off. The
    // AC 10 All/Blocked/Allowed control in the toolbar filters it instead.
    id: 'listState',
    header: 'Enforcement',
    accessor: 'listState',
    width: 140,
    renderCell: ({ row }) => (
      <PackageStateCell
        row={row}
        actions={actions.packageListActions}
        canEdit={actions.canEditPackageLists}
      />
    ),
  };
  return [
    {
      id: 'packageName',
      header: 'Package',
      accessor: 'packageName',
      width: 220,
      renderCell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{text(row.packageName)}</span>
          {row.knownBad && (
            <Badge variant="destructive" className="uppercase">
              Known bad
            </Badge>
          )}
        </div>
      ),
      sortable: true,
      sortField: 'packageName',
      ...stringFilter('packageName', organizationId, eventType),
    },
    {
      id: 'packageVersion',
      header: 'Version',
      description: PACKAGE_VERSION_TOOLTIP,
      accessor: 'packageVersion',
      width: 140,
      renderCell: ({ row }) => <span>{text(row.packageVersion)}</span>,
      sortable: true,
      sortField: 'packageVersion',
      ...stringFilter('packageVersion', organizationId, eventType),
    },
    {
      // KIR-3805: rich OSV cell, right after Version, sortable by severity
      // (Malicious → Critical → … → none). Not filterable.
      id: 'severity',
      header: 'Vulnerabilities (OSV)',
      accessor: 'osv',
      width: 260,
      renderCell: ({ row }) => <OsvVulnerabilityCell row={row} />,
      sortable: true,
      sortField: 'severity',
    },
    ...(actions.showPackageLists ? [listStateColumn] : []),
    {
      id: 'packageManager',
      header: 'Manager',
      accessor: 'packageManager',
      width: 110,
      renderCell: ({ row }) => <span>{text(row.packageManager)}</span>,
      sortable: true,
      sortField: 'packageManager',
      ...stringFilter('packageManager', organizationId, eventType),
    },
    {
      id: 'sourceUrl',
      header: 'Source',
      accessor: 'sourceUrl',
      width: 220,
      renderCell: ({ row }) => (
        <span className="block truncate">{text(row.sourceUrl)}</span>
      ),
      sortable: true,
      sortField: 'sourceUrl',
      ...stringFilter('sourceUrl', organizationId, eventType),
    },
    {
      // Multi-chip Agent column. Backend filter field is `agents` (array
      // overlap); the dropdown reuses the distinct agentName options endpoint.
      id: 'agents',
      header: 'Agent',
      accessor: 'agents',
      width: 180,
      renderCell: ({ row }) => <AgentsCell agents={row.agents} />,
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      filterOptions: {
        ...agentFilter.filterOptions,
        render: (option: FilterOption, isSelected: boolean) => (
          <AgentFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'userCount',
      header: 'Users',
      accessor: 'userCount',
      // Wide enough that the sort + filter icons don't crowd the next column.
      width: 130,
      // KIR-3833: project-wide clickable-users cell (same as the inventory
      // Plugins page) — Users icon + solid underline + tooltip.
      renderCell: ({ row }) => (
        <ClickableUserCount
          count={row.userCount}
          tooltipScope="with this package"
          onClick={e => {
            e.stopPropagation();
            actions.onShowUsers(row);
          }}
        />
      ),
      sortable: true,
      sortField: 'userCount',
      // KIR-3875: filter is a user-name value list (distinct emails), not a
      // count range — consistent with the other tables' Users filter. The
      // column still sorts/displays the count; only the filter targets identity.
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      filterOptions: userFilter.filterOptions,
    },
    {
      id: 'occurrenceCount',
      header: 'Occurrences',
      accessor: 'occurrenceCount',
      // Wide enough for the header + sort + filter icons (otherwise the filter
      // funnel crowds the next column, KIR-3807).
      width: 150,
      renderCell: ({ row }) => (
        <CountButton
          count={row.occurrenceCount}
          label={`Show ${row.occurrenceCount} occurrences of ${row.packageName ?? 'package'}`}
          onClick={() => actions.onShowOccurrences(row)}
        />
      ),
      sortable: true,
      sortField: 'occurrenceCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NUMBER_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
      range: { min: 1, max: 100, step: 1 },
    },
    {
      // KIR-3833: distinct endpoints (machines) the package was installed on.
      id: 'endpointCount',
      header: 'Endpoints',
      accessor: 'endpointCount',
      width: 140,
      renderCell: ({ row }) => (
        <CountButton
          count={row.endpointCount}
          label={`Show ${row.endpointCount} endpoints of ${row.packageName ?? 'package'}`}
          onClick={() => actions.onShowEndpoints(row)}
        />
      ),
      sortable: true,
      sortField: 'endpointCount',
      // KIR-3875: filter is an endpoint-name value list (distinct hostnames),
      // like inventory→users — not a count range. Column still sorts/displays
      // the count; only the filter targets endpoint identity.
      // KIR-5098: the shared endpoint name-or-ID filter (Contains / Equals),
      // suggesting this tab's hostnames; a machine ID still matches if typed.
      ...endpointFilterConfig({
        getQueryKey: endpointFilter.filterOptions.getQueryKey,
        queryFn: endpointFilter.filterOptions.queryFn,
      }),
    },
    {
      id: 'projectPath',
      header: 'Project',
      accessor: 'projectPath',
      width: 180,
      renderCell: ({ row }) => (
        <ProjectCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'projectPath',
      ...stringFilter('projectPath', organizationId, eventType),
    },
    {
      id: 'lastSeen',
      header: 'Last seen',
      accessor: 'lastSeen',
      // Format-aware width, like every other timestamp column.
      timestamp: true,
      renderCell: ({ row }) => <RelativeTimeWithTooltip date={row.lastSeen} />,
      sortable: true,
      sortField: 'lastSeen',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DATE_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
    },
  ];
}

export interface AggregatedRepositoryColumnActions {
  /** Open the unaggregated occurrences list for a repo group. */
  onShowOccurrences: (row: AggregatedRepository) => void;
  /** Open the distinct-users list for a repo group (KIR-3833). */
  onShowUsers: (row: AggregatedRepository) => void;
  /** Open the distinct-endpoints list for a repo group (KIR-3833). */
  onShowEndpoints: (row: AggregatedRepository) => void;
}

/**
 * Columns for the aggregated Repositories view (KIR-3807): one row per
 * repo+action+branch+project. Same shape as packages minus Vulnerabilities
 * (git access, not a package). Branch truncates so long refs don't overflow.
 */
export function createAggregatedRepositoryColumns(
  organizationId: string | undefined,
  actions: AggregatedRepositoryColumnActions
): ColumnConfig<AggregatedRepository>[] {
  const eventType: AgentCommandEventType = 'git';
  const agentFilter = stringFilter('agentName', organizationId, eventType);
  const userFilter = stringFilter('userEmail', organizationId, eventType);
  const endpointFilter = stringFilter(
    'endpointName',
    organizationId,
    eventType
  );
  return [
    {
      id: 'repoUrl',
      header: 'Repository',
      accessor: 'repoUrl',
      width: 280,
      renderCell: ({ row }) => (
        <div className="flex items-center gap-2">
          {/* min-w-0 lets the URL shrink so the Known bad badge is never
              pushed out of the (clipped) cell. */}
          <span className="block min-w-0 truncate font-medium">
            {text(row.repoUrl)}
          </span>
          {row.knownBad && (
            <Badge variant="destructive" className="shrink-0 uppercase">
              Known bad
            </Badge>
          )}
        </div>
      ),
      sortable: true,
      sortField: 'repoUrl',
      ...stringFilter('repoUrl', organizationId, eventType),
    },
    {
      id: 'action',
      header: 'Action',
      accessor: 'action',
      width: 100,
      renderCell: ({ row }) => <span>{text(row.action)}</span>,
      sortable: true,
      sortField: 'action',
      ...stringFilter('action', organizationId, eventType),
    },
    {
      id: 'branch',
      header: 'Branch',
      accessor: 'branch',
      width: 160,
      // Truncate long branch names so they don't overflow into the next column.
      renderCell: ({ row }) => <BranchCell branch={row.branch} />,
      sortable: true,
      sortField: 'branch',
      ...stringFilter('branch', organizationId, eventType),
    },
    {
      id: 'agents',
      header: 'Agent',
      accessor: 'agents',
      width: 180,
      renderCell: ({ row }) => <AgentsCell agents={row.agents} />,
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      filterOptions: {
        ...agentFilter.filterOptions,
        render: (option: FilterOption, isSelected: boolean) => (
          <AgentFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'userCount',
      header: 'Users',
      accessor: 'userCount',
      width: 130,
      // KIR-3833: project-wide clickable-users cell (same as the inventory
      // Plugins page) — Users icon + solid underline + tooltip.
      renderCell: ({ row }) => (
        <ClickableUserCount
          count={row.userCount}
          tooltipScope="with this repository"
          onClick={e => {
            e.stopPropagation();
            actions.onShowUsers(row);
          }}
        />
      ),
      sortable: true,
      sortField: 'userCount',
      // KIR-3875: filter is a user-name value list (distinct emails), not a
      // count range — consistent with the other tables' Users filter. The
      // column still sorts/displays the count; only the filter targets identity.
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      filterOptions: userFilter.filterOptions,
    },
    {
      id: 'occurrenceCount',
      header: 'Occurrences',
      accessor: 'occurrenceCount',
      width: 150,
      renderCell: ({ row }) => (
        <CountButton
          count={row.occurrenceCount}
          label={`Show ${row.occurrenceCount} occurrences of ${row.repoUrl ?? 'repository'}`}
          onClick={() => actions.onShowOccurrences(row)}
        />
      ),
      sortable: true,
      sortField: 'occurrenceCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NUMBER_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
      range: { min: 1, max: 100, step: 1 },
    },
    {
      // KIR-3833: distinct endpoints (machines) the repo was accessed from.
      id: 'endpointCount',
      header: 'Endpoints',
      accessor: 'endpointCount',
      width: 140,
      renderCell: ({ row }) => (
        <CountButton
          count={row.endpointCount}
          label={`Show ${row.endpointCount} endpoints of ${row.repoUrl ?? 'repository'}`}
          onClick={() => actions.onShowEndpoints(row)}
        />
      ),
      sortable: true,
      sortField: 'endpointCount',
      // KIR-3875: filter is an endpoint-name value list (distinct hostnames),
      // like inventory→users — not a count range.
      // KIR-5098: the shared endpoint name-or-ID filter (Contains / Equals),
      // suggesting this tab's hostnames; a machine ID still matches if typed.
      ...endpointFilterConfig({
        getQueryKey: endpointFilter.filterOptions.getQueryKey,
        queryFn: endpointFilter.filterOptions.queryFn,
      }),
    },
    {
      id: 'projectPath',
      header: 'Project',
      accessor: 'projectPath',
      width: 180,
      renderCell: ({ row }) => (
        <ProjectCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'projectPath',
      ...stringFilter('projectPath', organizationId, eventType),
    },
    {
      id: 'lastSeen',
      header: 'Last seen',
      accessor: 'lastSeen',
      // Format-aware width, like every other timestamp column.
      timestamp: true,
      renderCell: ({ row }) => <RelativeTimeWithTooltip date={row.lastSeen} />,
      sortable: true,
      sortField: 'lastSeen',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DATE_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
    },
  ];
}
